/* ============================================================
   VELOX — site behaviour (Shopify theme)
   Waitlist forms are Shopify customer forms: signups become customers
   subscribed to email marketing, tagged waitlist / source / segment / ref / tel.
   ============================================================ */

(function () {
  "use strict";

  /* ---------- mobile nav ---------- */
  const toggle = document.querySelector(".nav__toggle");
  const links = document.querySelector(".nav__links");
  if (toggle && links) {
    toggle.addEventListener("click", () => {
      const open = links.classList.toggle("open");
      document.body.classList.toggle("nav-open", open);
      toggle.setAttribute("aria-expanded", String(open));
    });
    links.querySelectorAll("a").forEach((a) =>
      a.addEventListener("click", () => {
        links.classList.remove("open");
        document.body.classList.remove("nav-open");
      })
    );
  }

  /* ---------- optional dropped-in logo ----------
     If a file exists at assets/logo/velox-logo.png (any of the common
     names below), swap it in for the text wordmark automatically. */
  document.querySelectorAll(".brand").forEach((brand) => {
    const img = brand.querySelector(".brand__logo");
    const mark = brand.querySelector(".brand__mark");
    if (!img) return;
    const test = new Image();
    // show the icon mark alongside the wordmark (lockup); keep the text visible
    test.onload = () => { img.src = test.src; img.style.display = "block"; };
    test.src = img.getAttribute("data-src") || "assets/logo/velox-logo.png";
  });

  /* ---------- scroll reveal + staggered grids ---------- */
  const revs = document.querySelectorAll(".reveal, .stagger");
  if ("IntersectionObserver" in window && revs.length) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    revs.forEach((el) => io.observe(el));
  } else {
    revs.forEach((el) => el.classList.add("in"));
  }

  /* ---------- hero splash: scroll-driven drift + rotate ---------- */
  const splash = document.querySelector(".hero__splash");
  // The hero splash is a looping video. Hold it on the poster frame for anyone
  // who has asked for reduced motion; otherwise make sure it actually plays —
  // the autoplay attribute alone isn't reliable (blocked autoplay, battery
  // saver, or a tab that loaded in the background all leave it frozen).
  if (splash && splash.tagName === "VIDEO") {
    const stillWants = () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!stillWants()) {
      splash.removeAttribute("autoplay");
      splash.pause();
    } else {
      splash.muted = true;              // required for autoplay; set as a property, not just the attribute
      splash.playsInline = true;
      const tryPlay = () => { const p = splash.play(); if (p && p.catch) p.catch(() => {}); };

      tryPlay();
      splash.addEventListener("loadeddata", tryPlay, { once: true });
      splash.addEventListener("canplay", tryPlay, { once: true });

      // If the browser refused, start on the visitor's first interaction.
      ["pointerdown", "touchstart", "keydown", "scroll"].forEach((ev) =>
        window.addEventListener(ev, function once() {
          window.removeEventListener(ev, once);
          if (splash.paused) tryPlay();
        }, { passive: true })
      );

      // Nudge it into playing when it scrolls into view. Deliberately never
      // pauses: a false "not intersecting" would freeze the hero for good,
      // which is a worse failure than letting a 2.7MB loop keep running.
      if ("IntersectionObserver" in window) {
        new IntersectionObserver((entries) => entries.forEach((e) => {
          if (e.isIntersecting && stillWants() && splash.paused) tryPlay();
        }), { threshold: 0.1 }).observe(splash);
      }
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden && stillWants() && splash.paused) tryPlay();
      });
    }
  }
  if (splash && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    let ticking = false;
    // A transparent cutout can drift a long way and still read as depth. A
    // hard-edged video can't — too much and it just looks misaligned with its
    // slot, so it drifts less, is capped, and sits still once stacked on mobile.
    const isVideo = splash.tagName === "VIDEO";
    const factor = isVideo ? 0.05 : 0.16;
    const maxDrift = isVideo ? 28 : Infinity;
    const update = () => {
      ticking = false;
      if (isVideo && window.innerWidth < 900) { splash.style.transform = ""; return; }
      const y = window.scrollY || window.pageYOffset || 0;
      // gentle parallax: splash lags the page scroll on its own layer
      splash.style.transform = "translate3d(0," + Math.min(y * factor, maxDrift) + "px,0)";
    };
    window.addEventListener("scroll", () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- product gallery (product page) ---------- */
  const galMain = document.querySelector("[data-gallery-main]");
  const thumbs = document.querySelectorAll("[data-gallery-thumb]");
  if (galMain && thumbs.length) {
    thumbs.forEach((t) => {
      t.addEventListener("click", () => {
        thumbs.forEach((x) => x.classList.remove("is-active"));
        t.classList.add("is-active");
        const type = t.getAttribute("data-type");
        const src = t.getAttribute("data-full");
        if (type === "video") {
          galMain.innerHTML =
            '<video src="' + src + '" autoplay muted loop playsinline></video>';
        } else {
          galMain.innerHTML = '<img src="' + src + '" alt="Velox Hydro">';
        }
      });
    });
  }

  /* ---------- waitlist modal ---------- */
  const modal = document.getElementById("waitlist-modal");
  function openModal(source) {
    if (!modal) return;
    modal.classList.add("open");
    const src = modal.querySelector('input[name="source"]');
    if (src) src.value = source || "modal";
    const first = modal.querySelector('input[name="name"]') || modal.querySelector('input[type="email"]');
    if (first) setTimeout(() => first.focus(), 60);
  }
  function closeModal() { if (modal) modal.classList.remove("open"); }
  document.querySelectorAll("[data-open-waitlist]").forEach((b) =>
    b.addEventListener("click", (e) => { e.preventDefault(); openModal(b.getAttribute("data-open-waitlist")); })
  );
  if (modal) {
    modal.querySelectorAll("[data-close-modal]").forEach((b) => b.addEventListener("click", closeModal));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeModal(); });
  }

  /* ---------- referral codes (coach / influencer links) ----------
     A coach shares veloxhydro.com/?ref=THEIRCODE. We remember the code for
     60 days so it still attaches if the visitor comes back later and signs
     up from a different page. */
  const REF_KEY = "velox_ref";
  const REF_TTL = 60 * 24 * 3600 * 1000;
  const cleanRef = (v) => (v || "").toString().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 24);
  try {
    const fromUrl = cleanRef(new URLSearchParams(location.search).get("ref"));
    if (fromUrl) localStorage.setItem(REF_KEY, JSON.stringify({ code: fromUrl, ts: Date.now() }));
  } catch (_) {}
  function currentRef() {
    try {
      const o = JSON.parse(localStorage.getItem(REF_KEY) || "null");
      if (!o || !o.code) return "";
      if (Date.now() - (o.ts || 0) > REF_TTL) { localStorage.removeItem(REF_KEY); return ""; }
      return cleanRef(o.code);
    } catch (_) { return ""; }
  }

  /* ---------- waitlist + contact submit (all .wl-form forms) ----------
     Runs in the capture phase, before Shopify's own submit handling: an invalid
     form is stopped here; a valid one gets its hidden fields filled and is then
     left alone, so Shopify's spam check (hCaptcha) and native post run as normal.
     Shopify redirects back with ?customer_posted=true (or ?contact_posted=true)
     and the Liquid snippet renders the result. Filling is idempotent because
     Shopify may re-submit the form after its captcha resolves. */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Mobile: optional leading +, then 7–15 digits once spaces/dashes/brackets are stripped.
  const PHONE_RE = /^\+?\d{7,15}$/;
  const cleanPhone = (v) => v.replace(/[\s\-().]/g, "");
  const tagSafe = (v) => (v || "").toString().replace(/,/g, " ").trim();
  document.addEventListener("submit", (e) => {
    const form = e.target;
    // forms marked data-native (store password, search) only borrow the look — they submit untouched
    if (!(form instanceof HTMLFormElement) || !form.matches(".wl-form:not([data-native])")) return;
    const msg = form.querySelector(".wl-msg");
    const isContact = form.getAttribute("data-kind") === "contact";
    const field = (n) => form.querySelector('[name="' + n + '"]');
    const nameEl = field(isContact ? "contact[name]" : "name");
    const emailEl = field("contact[email]");
    const phoneEl = field(isContact ? "contact[phone]" : "phone");
    const name = (nameEl && nameEl.value || "").trim();
    const email = (emailEl && emailEl.value || "").trim();
    const phone = cleanPhone((phoneEl && phoneEl.value || "").trim());
    const stop = (text) => { e.preventDefault(); e.stopImmediatePropagation(); setMsg(msg, text, "err"); };
    if (name.length < 2) return stop("Enter your name.");
    if (!EMAIL_RE.test(email)) return stop("Enter a valid email.");
    if (!PHONE_RE.test(phone)) return stop("Enter a valid mobile number with country code (e.g. +971…).");
    if (phoneEl) phoneEl.value = phone;

    if (isContact) {
      const refEl = field("contact[referral_code]");
      if (refEl) refEl.value = currentRef();
    } else {
      // Shopify customers have first/last name; split "Ye Lin Kyaw" -> "Ye Lin" + "Kyaw".
      const parts = name.split(/\s+/);
      const last = parts.length > 1 ? parts.pop() : "";
      field("contact[first_name]").value = parts.join(" ");
      field("contact[last_name]").value = last;

      const seg = form.querySelector('input[name="segment"]:checked');
      const srcEl = field("source");
      const source = tagSafe((srcEl && srcEl.value) || form.getAttribute("data-source") || "site");
      // "waitlist" before launch, "newsletter" after (set by the snippet from the Pre-launch mode setting)
      const tags = [tagSafe(form.getAttribute("data-list")) || "waitlist", "source:" + source];
      if (seg) tags.push("segment:" + tagSafe(seg.value));
      const ref = currentRef();
      if (ref) tags.push("ref:" + ref);
      tags.push("tel:" + phone);
      field("contact[tags]").value = tags.join(",");
    }
    setMsg(msg, "Sending…", "");
  }, true);
  function setMsg(el, text, kind) { if (!el) return; el.textContent = text; el.className = "wl-msg " + (kind || ""); }

  /* ---------- after a Shopify form post: reopen the modal it came from ---------- */
  if (/[?&](customer|contact)_posted=/.test(location.search) && location.hash) {
    const posted = document.getElementById(location.hash.slice(1));
    if (posted && modal && modal.contains(posted)) modal.classList.add("open");
    else if (posted) posted.scrollIntoView({ block: "center" });
  }

  /* ---------- year in footer ---------- */
  document.querySelectorAll("[data-year]").forEach((el) => (el.textContent = new Date().getFullYear()));
})();
